import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CirclePause,
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Target,
  Trash2,
  Upload,
  UserRoundX,
  Users,
  X
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { trackerApi, trackerDownload } from '../services/projectTrackerApi';
import { useAuth } from '../context/AuthContext.jsx';
import Layout from '../components/Layout.jsx';
import '../styles/projectTracker.css';

const healthColors = {
  Red: '#ef4444',
  Amber: '#f59e0b',
  Green: '#22c55e',
  Blue: '#3b82f6',
  Grey: '#94a3b8'
};

const healthDescriptions = {
  Red: 'Overdue - action required',
  Amber: 'Due within seven days',
  Green: 'On track or completed',
  Blue: 'On hold or blocked',
  Grey: 'No target or not started'
};

const blankProject = {
  project_code: '',
  client: '',
  project_name: '',
  project_owner: '',
  priority: 'Medium',
  issue: '',
  status: 'Not Started',
  original_target_date: '',
  revised_target_date: '',
  actual_closure_date: ''
};

function Toast({ toast, onClose }) {
  if (!toast) return null;
  return (
    <div className={`toast ${toast.type || 'success'}`}>
      <span>{toast.message}</span>
      <button onClick={onClose}><X size={16} /></button>
    </div>
  );
}
function TrackerLogin({ setSession, onLogin }) {
  const [email, setEmail] = useState('admin@tracker.local');
  const [password, setPassword] = useState('Admin@123');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await trackerApi('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      setSession(result.token, result.user);
      onLogin(result.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="brand-mark"><FolderKanban size={32} /></div>
        <p className="eyebrow">BuildINT AI</p>
        <h1>Automated Project Tracker</h1>
        <p className="muted">Track ownership, target dates, delays and project health from one dashboard.</p>
        <form onSubmit={submit}>
          <label>Email<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
          <label>Password<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" required /></label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary wide" disabled={loading}>{loading ? 'Signing in...' : 'Sign in'}</button>
        </form>
        <div className="login-hint"><ShieldCheck size={16} /> Default local admin credentials are already filled.</div>
      </div>
    </div>
  );
}
function TrackerSidebar({ open, setOpen, page, setPage }) {
  const items = [
    ['dashboard', LayoutDashboard, 'Dashboard'],
    ['projects', FolderKanban, 'Projects'],
    ['owners', Users, 'Owner Scorecard']
  ];

  return (
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-mark small"><FolderKanban size={23} /></div>
        <div><strong>Project Tracker</strong><span>Automation Portal</span></div>
      </div>
      <nav>
        {items.map(([key, Icon, label]) => (
          <button
            key={key}
            className={page === key ? 'active' : ''}
            onClick={() => { setPage(key); setOpen(false); }}
          >
            <Icon size={19} /><span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-foot">React + Node.js<br />Automated PDF reporting</div>
    </aside>
  );
}
function TrackerTopbar({ user, setSidebarOpen, onLogout }) {
  return (
    <header className="topbar">
      <button className="icon-button mobile-only" onClick={() => setSidebarOpen((value) => !value)}><Menu size={21} /></button>
      <div><p className="eyebrow">PROJECT MANAGEMENT OFFICE</p><strong>Daily Tracker</strong></div>
      <div className="topbar-actions">
        <div className="user-chip">
          <span>{user?.name?.[0] || 'A'}</span>
          <div><strong>{user?.name}</strong><small>{user?.role}</small></div>
        </div>
        <button className="icon-button" title="Logout" onClick={onLogout}><LogOut size={19} /></button>
      </div>
    </header>
  );
}

function KpiCard({ icon: Icon, label, value, tone, detail }) {
  return (
    <div className={`kpi-card ${tone}`}>
      <div className="kpi-icon"><Icon size={22} /></div>
      <div><span>{label}</span><strong>{value ?? 0}</strong><small>{detail}</small></div>
    </div>
  );
}

function HealthBadge({ value }) {
  return <span className={`health-badge ${String(value || 'Grey').toLowerCase()}`}><i />{value || 'Grey'}</span>;
}

function StatusBadge({ value }) {
  const key = String(value || 'Not Started').toLowerCase().replace(/\s+/g, '-');
  return <span className={`status-badge ${key}`}>{value || 'Not Started'}</span>;
}

function displayDate(value) {
  if (!value) return 'Not confirmed';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function delayText(project, dueSoon = false) {
  if (project.delay_days === null || project.delay_days === undefined) return 'No target';
  if (project.delay_days > 0) return `${project.delay_days} days overdue`;
  if (dueSoon || project.delay_days <= 0) return `${Math.abs(project.delay_days)} days left`;
  return `${project.delay_days} days`;
}

function HealthTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  const visible = (item.projects || []).slice(0, 5);
  return (
    <div className="health-tooltip">
      <div className="health-tooltip-title">
        <i style={{ background: healthColors[item.name] }} />
        <strong>{item.name}: {item.value} project{item.value === 1 ? '' : 's'}</strong>
      </div>
      <p>{item.percentage}% of the total portfolio</p>
      {visible.map((project) => <span key={project.id}>{project.project_name}</span>)}
      {(item.projects || []).length > visible.length && <small>+{item.projects.length - visible.length} more</small>}
    </div>
  );
}

function HealthSummaryItem({ item }) {
  const visibleProjects = (item.projects || []).slice(0, item.name === 'Red' ? 4 : 2);
  return (
    <div className={`health-summary-item ${item.name.toLowerCase()}`}>
      <div className="health-summary-top">
        <div><i style={{ background: healthColors[item.name] }} /><strong>{item.name}</strong></div>
        <b>{item.value}</b>
      </div>
      <p>{healthDescriptions[item.name]} - {item.percentage}%</p>
      <div className="health-project-names">
        {visibleProjects.map((project) => <span key={project.id}>{project.project_name}</span>)}
        {!visibleProjects.length && <span>No matching projects</span>}
        {(item.projects || []).length > visibleProjects.length && <small>+{item.projects.length - visibleProjects.length} more projects</small>}
      </div>
    </div>
  );
}

function PriorityBadge({ value }) {
  return <span className={`priority ${String(value || 'Medium').toLowerCase()}`}>{value || 'Medium'}</span>;
}

function ActionProject({ project, mode }) {
  return (
    <div className="action-project">
      <div className="action-project-main">
        <strong>{project.project_name}</strong>
        <span>{project.client}</span>
      </div>
      <div className="action-project-meta">
        <span><Users size={13} />{project.project_owner || 'Unassigned'}</span>
        <span><Target size={13} />{displayDate(project.target_date)}</span>
        <b className={mode === 'red' ? 'danger-text' : 'amber-text'}>{delayText(project, mode === 'amber')}</b>
      </div>
      <div className="action-project-footer">
        <PriorityBadge value={project.priority} />
        <p>{project.issue || 'No current update provided.'}</p>
      </div>
    </div>
  );
}

function Dashboard({ refreshKey, user, notify }) {
  const [data, setData] = useState(null);
  const [reportSettings, setReportSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const canEdit = ['admin', 'manager'].includes(user.role);

  useEffect(() => {
    setLoading(true);
    Promise.all([trackerApi('/dashboard'), trackerApi('/report-settings')])
      .then(([dashboard, settings]) => {
        setData(dashboard);
        setReportSettings(settings);
      })
      .catch((error) => notify(error.message, 'error'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const sendTestReport = async () => {
    setSending(true);
    try {
      const result = await trackerApi('/report-email/test', { method: 'POST', body: JSON.stringify({}) });
      notify(result.message);
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Loading />;
  const stats = data?.stats || {};
  const healthSummary = data?.healthSummary || [];
  const scheduleReady = reportSettings?.enabled && reportSettings?.configured;

  return (
    <div className="page-content">
      <PageTitle
        title="Executive Dashboard"
        subtitle="Overall portfolio clarity with project names, ownership, deadlines and action priorities."
        actions={(
          <>
            <button className="secondary" onClick={() => trackerDownload('/dashboard.pdf', 'Project_Tracker_Dashboard.pdf')}>
              <FileText size={17} />Download PDF
            </button>
            {canEdit && (
              <button className="primary" onClick={sendTestReport} disabled={sending || !reportSettings?.configured}>
                <Send size={17} />{sending ? 'Sending...' : 'Send Test Report'}
              </button>
            )}
          </>
        )}
      />

      <div className={`report-strip ${scheduleReady ? 'ready' : 'setup'}`}>
        <div className="report-strip-icon"><Mail size={20} /></div>
        <div>
          <strong>Automatic daily dashboard PDF</strong>
          <span>
            Scheduled for {reportSettings?.displayTime || '10:00 AM'} ({reportSettings?.timezone || 'Asia/Kolkata'}) to{' '}
            <b>{reportSettings?.recipient || 'krutiksha.bawa@buildint.co'}</b>
          </span>
        </div>
        <div className="report-state">
          {scheduleReady ? 'Automatic delivery active' : reportSettings?.configured ? 'Enable EMAIL_ENABLED' : 'SMTP setup required'}
        </div>
      </div>

      <div className="kpi-grid six">
        <KpiCard icon={BriefcaseBusiness} label="Total Projects" value={stats.total} tone="slate" detail={`${stats.open || 0} currently open`} />
        <KpiCard icon={Clock3} label="Open Projects" value={stats.open} tone="blue" detail="Current delivery workload" />
        <KpiCard icon={CheckCircle2} label="Completed" value={stats.completed} tone="green" detail={`${stats.completionRate || 0}% completion rate`} />
        <KpiCard icon={AlertTriangle} label="Overdue / Red" value={stats.overdue} tone="red" detail="Immediate management action" />
        <KpiCard icon={CalendarClock} label="Due Soon / Amber" value={stats.dueSoon} tone="amber" detail="Target within seven days" />
        <KpiCard icon={CirclePause} label="On Hold" value={stats.onHold} tone="blue" detail="Paused or blocked" />
      </div>

      <div className="management-snapshot">
        <div><Target size={18} /><span><strong>{stats.revisedTargets || 0}</strong> revised targets</span></div>
        <div><UserRoundX size={18} /><span><strong>{stats.unassigned || 0}</strong> unassigned projects</span></div>
        <div><AlertTriangle size={18} /><span><strong>{stats.highPriorityOpen || 0}</strong> high/critical open</span></div>
        <div><FolderKanban size={18} /><span><strong>{stats.notStarted || 0}</strong> not started</span></div>
      </div>

      <div className="dashboard-chart-grid">
        <section className="panel health-panel">
          <div className="panel-head">
            <div><h3>Project Health With Project Names</h3><p>RAG classification with the projects included in every category.</p></div>
            <BarChart3 size={20} />
          </div>
          <div className="health-chart-layout">
            <div className="donut-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={healthSummary}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={72}
                    outerRadius={108}
                    paddingAngle={3}
                  >
                    {healthSummary.map((entry) => <Cell key={entry.name} fill={healthColors[entry.name]} />)}
                  </Pie>
                  <Tooltip content={<HealthTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="donut-center"><strong>{stats.total || 0}</strong><span>Total Projects</span></div>
            </div>
            <div className="health-summary-list">
              {healthSummary.map((item) => <HealthSummaryItem key={item.name} item={item} />)}
            </div>
          </div>
        </section>

        <section className="panel owner-chart-panel">
          <div className="panel-head">
            <div><h3>Owner Workload</h3><p>Total assignments and workload concentration by owner.</p></div>
            <Users size={20} />
          </div>
          <div className="owner-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data?.owners || []).slice(0, 9)} margin={{ left: -12, right: 8, top: 12 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="owner" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="owner-risk-list">
            {(data?.owners || []).slice(0, 5).map((owner) => (
              <div key={owner.owner}>
                <span><strong>{owner.owner}</strong>{owner.total} projects</span>
                <b className={owner.overdue ? 'danger-text' : ''}>{owner.overdue} overdue</b>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="action-grid">
        <section className="panel action-panel red-panel">
          <div className="panel-head">
            <div><h3>Immediate Attention - Red Projects</h3><p>Most overdue projects, including owner, target, priority and current issue.</p></div>
            <span className="panel-count red">{data?.attention?.length || 0}</span>
          </div>
          <div className="action-list">
            {(data?.attention || []).slice(0, 8).map((project) => <ActionProject key={project.id} project={project} mode="red" />)}
            {!data?.attention?.length && <div className="clear-state"><CheckCircle2 size={25} /><strong>No overdue projects</strong><span>All projects are within their target dates.</span></div>}
          </div>
        </section>

        <section className="panel action-panel amber-panel">
          <div className="panel-head">
            <div><h3>Due Soon - Amber Projects</h3><p>Projects requiring follow-up before the target date is missed.</p></div>
            <span className="panel-count amber">{data?.dueSoonProjects?.length || 0}</span>
          </div>
          <div className="action-list">
            {(data?.dueSoonProjects || []).slice(0, 8).map((project) => <ActionProject key={project.id} project={project} mode="amber" />)}
            {!data?.dueSoonProjects?.length && <div className="clear-state"><CheckCircle2 size={25} /><strong>No projects due soon</strong><span>No target dates fall within the next seven days.</span></div>}
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head"><div><h3>Recently Updated Projects</h3><p>Latest records from the project register.</p></div></div>
        <div className="compact-table">
          <table>
            <thead><tr><th>ID</th><th>Client / Project</th><th>Owner</th><th>Status</th><th>Target</th><th>Delay</th><th>Health</th></tr></thead>
            <tbody>
              {(data?.recent || []).map((project) => (
                <tr key={project.id}>
                  <td>{project.project_code || '-'}</td>
                  <td><strong>{project.project_name}</strong><small>{project.client}</small></td>
                  <td>{project.project_owner || 'Unassigned'}</td>
                  <td><StatusBadge value={project.status} /></td>
                  <td>{displayDate(project.target_date)}</td>
                  <td className={project.delay_days > 0 ? 'danger-text' : ''}>{delayText(project)}</td>
                  <td><HealthBadge value={project.health} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ProjectModal({ project, options, onClose, onSaved }) {
  const [form, setForm] = useState(project ? { ...project } : { ...blankProject });
  const [saving, setSaving] = useState(false);
  const change = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await trackerApi(project ? `/projects/${project.id}` : '/projects', {
        method: project ? 'PUT' : 'POST',
        body: JSON.stringify(form)
      });
      onSaved(project ? 'Project updated successfully.' : 'Project created successfully.');
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal-card">
        <div className="modal-head">
          <div><h2>{project ? 'Edit Project' : 'Add Project'}</h2><p>Health and delay are calculated automatically.</p></div>
          <button className="icon-button" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="project-form">
          <div className="form-grid">
            <label>Project ID<input value={form.project_code || ''} onChange={(event) => change('project_code', event.target.value)} placeholder="e.g. 27" /></label>
            <label>Priority<select value={form.priority || 'Medium'} onChange={(event) => change('priority', event.target.value)}><option>Critical</option><option>High</option><option>Medium</option><option>Low</option></select></label>
            <label>Client *<input value={form.client || ''} onChange={(event) => change('client', event.target.value)} required /></label>
            <label>Project Name *<input value={form.project_name || ''} onChange={(event) => change('project_name', event.target.value)} required /></label>
            <label>Project Owner<input list="owner-list" value={form.project_owner || ''} onChange={(event) => change('project_owner', event.target.value)} /><datalist id="owner-list">{(options.owners || []).map((value) => <option value={value} key={value} />)}</datalist></label>
            <label>Status<select value={form.status || 'Not Started'} onChange={(event) => change('status', event.target.value)}>{['Not Started', 'WIP', 'On Hold', 'Completed', 'Closed'].map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Original Target Date<input type="date" value={form.original_target_date || ''} onChange={(event) => change('original_target_date', event.target.value)} /></label>
            <label>Revised Target Date<input type="date" value={form.revised_target_date || ''} onChange={(event) => change('revised_target_date', event.target.value)} /></label>
            <label>Actual Closure Date<input type="date" value={form.actual_closure_date || ''} onChange={(event) => change('actual_closure_date', event.target.value)} /></label>
          </div>
          <label>Issue / Current Update<textarea rows="6" value={form.issue || ''} onChange={(event) => change('issue', event.target.value)} placeholder="Add latest project update, blocker and next action." /></label>
          <div className="modal-actions"><button type="button" className="secondary" onClick={onClose}>Cancel</button><button className="primary" disabled={saving}>{saving ? 'Saving...' : 'Save Project'}</button></div>
        </form>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [sheetName, setSheetName] = useState('DATA_ENTRY');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    form.append('sheetName', sheetName);
    form.append('replaceExisting', String(replaceExisting));
    try {
      const result = await trackerApi('/import', { method: 'POST', body: form });
      onImported(result);
    } catch (error) {
      alert(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card small-modal">
        <div className="modal-head"><div><h2>Import Excel Tracker</h2><p>Import rows from your main project sheet.</p></div><button className="icon-button" onClick={onClose}><X size={20} /></button></div>
        <form onSubmit={submit} className="project-form">
          <label>Excel file<input type="file" accept=".xlsx,.xlsm" onChange={(event) => setFile(event.target.files?.[0] || null)} required /></label>
          <label>Sheet name<input value={sheetName} onChange={(event) => setSheetName(event.target.value)} /></label>
          <label className="checkbox"><input type="checkbox" checked={replaceExisting} onChange={(event) => setReplaceExisting(event.target.checked)} /><span>Replace all existing project records</span></label>
          <div className="warning-box"><AlertTriangle size={18} /><span>Keep this unchecked to append projects. Duplicate Project IDs will be highlighted after import.</span></div>
          <div className="modal-actions"><button type="button" className="secondary" onClick={onClose}>Cancel</button><button className="primary" disabled={busy}>{busy ? 'Importing...' : 'Import Projects'}</button></div>
        </form>
      </div>
    </div>
  );
}

function Projects({ refreshKey, setRefreshKey, notify, user }) {
  const [projects, setProjects] = useState([]);
  const [options, setOptions] = useState({ owners: [], statuses: [], priorities: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '', owner: '', priority: '', health: '' });
  const [modal, setModal] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.entries(filters).filter(([, value]) => value));
      const [list, opts] = await Promise.all([trackerApi(`/projects?${params}`), trackerApi('/options')]);
      setProjects(list.projects);
      setOptions(opts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [filters, refreshKey]);
  useEffect(() => setPage(1), [filters]);

  const pages = Math.max(1, Math.ceil(projects.length / pageSize));
  const visible = projects.slice((page - 1) * pageSize, page * pageSize);
  const canEdit = ['admin', 'manager'].includes(user.role);

  const remove = async (project) => {
    if (!window.confirm(`Delete ${project.project_name}?`)) return;
    await trackerApi(`/projects/${project.id}`, { method: 'DELETE' });
    notify('Project deleted.', 'success');
    setRefreshKey((value) => value + 1);
  };

  return (
    <div className="page-content">
      <PageTitle
        title="Project Register"
        subtitle={`${projects.length} matching projects with automatic health and delay calculation.`}
        actions={(
          <>
            <button className="secondary" onClick={() => trackerDownload('/export.xlsx', 'Project_Tracker.xlsx')}><Download size={17} />Export Excel</button>
            <button className="secondary" onClick={() => trackerDownload('/dashboard.pdf', 'Project_Tracker_Dashboard.pdf')}><FileText size={17} />Dashboard PDF</button>
            {canEdit && <button className="secondary" onClick={() => trackerDownload('/backup', 'project-tracker-backup.sqlite')}><ShieldCheck size={17} />Backup</button>}
            {canEdit && <button className="secondary" onClick={() => setShowImport(true)}><Upload size={17} />Import Excel</button>}
            {canEdit && <button className="primary" onClick={() => setModal('new')}><Plus size={17} />Add Project</button>}
          </>
        )}
      />
      <section className="panel filter-panel">
        <div className="search-box"><Search size={18} /><input value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} placeholder="Search ID, client, project, owner or issue..." /></div>
        <div className="filters">
          <Filter size={17} />
          <select value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}><option value="">All Statuses</option>{options.statuses.map((value) => <option key={value}>{value}</option>)}</select>
          <select value={filters.owner} onChange={(event) => setFilters({ ...filters, owner: event.target.value })}><option value="">All Owners</option>{options.owners.map((value) => <option key={value}>{value}</option>)}</select>
          <select value={filters.priority} onChange={(event) => setFilters({ ...filters, priority: event.target.value })}><option value="">All Priorities</option>{options.priorities.map((value) => <option key={value}>{value}</option>)}</select>
          <select value={filters.health} onChange={(event) => setFilters({ ...filters, health: event.target.value })}><option value="">All Health</option>{['Red', 'Amber', 'Green', 'Blue', 'Grey'].map((value) => <option key={value}>{value}</option>)}</select>
        </div>
      </section>
      <section className="panel table-panel">
        {loading ? <Loading /> : (
          <div className="table-scroll">
            <table className="project-table">
              <thead><tr><th>Project</th><th>Owner</th><th>Priority</th><th>Status</th><th>Target Date</th><th>Delay</th><th>Health</th>{canEdit && <th>Actions</th>}</tr></thead>
              <tbody>
                {visible.map((project) => (
                  <tr key={project.id}>
                    <td className="project-cell">
                      <div><span className="project-id">#{project.project_code || project.id}</span>{project.duplicate_project_id && <span className="duplicate-tag">Duplicate ID</span>}</div>
                      <strong>{project.project_name}</strong><small>{project.client}</small><p>{project.issue || 'No current update'}</p>
                    </td>
                    <td>{project.project_owner || <span className="muted">Unassigned</span>}</td>
                    <td><PriorityBadge value={project.priority} /></td>
                    <td><StatusBadge value={project.status} /></td>
                    <td>{displayDate(project.target_date)}{project.revised_target_date && <small>Revised</small>}</td>
                    <td className={project.delay_days > 0 ? 'danger-text' : ''}>{delayText(project)}</td>
                    <td><HealthBadge value={project.health} /></td>
                    {canEdit && <td><div className="row-actions"><button title="Edit" onClick={() => setModal(project)}><Pencil size={16} /></button><button className="danger" title="Delete" onClick={() => remove(project)}><Trash2 size={16} /></button></div></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {!visible.length && <Empty />}
          </div>
        )}
        <div className="pagination"><span>Page {page} of {pages}</span><div><button disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={17} /></button><button disabled={page === pages} onClick={() => setPage((value) => value + 1)}><ChevronRight size={17} /></button></div></div>
      </section>
      {modal && <ProjectModal project={modal === 'new' ? null : modal} options={options} onClose={() => setModal(null)} onSaved={(message) => { setModal(null); notify(message); setRefreshKey((value) => value + 1); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onImported={(result) => { setShowImport(false); notify(`Imported ${result.imported} projects${result.warnings.length ? ` with ${result.warnings.length} duplicate warning(s)` : ''}.`); setRefreshKey((value) => value + 1); }} />}
    </div>
  );
}

function Owners({ refreshKey }) {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { trackerApi('/dashboard').then((data) => setOwners(data.owners)).finally(() => setLoading(false)); }, [refreshKey]);

  return (
    <div className="page-content">
      <PageTitle title="Owner Scorecard" subtitle="Workload, overdue items, due-soon projects and completion rate by project owner." />
      {loading ? <Loading /> : (
        <>
          <div className="owner-cards">
            {owners.slice(0, 4).map((owner) => (
              <div className="owner-card" key={owner.owner}>
                <div className="avatar">{owner.owner[0]}</div>
                <div><strong>{owner.owner}</strong><span>{owner.total} projects - {owner.overdue} overdue</span></div>
                <b>{owner.completionRate}%</b>
              </div>
            ))}
          </div>
          <section className="panel table-panel">
            <div className="table-scroll">
              <table className="project-table">
                <thead><tr><th>Owner</th><th>Total Projects</th><th>Open</th><th>Overdue</th><th>Due Soon</th><th>Completed</th><th>Completion Rate</th></tr></thead>
                <tbody>
                  {owners.map((owner) => (
                    <tr key={owner.owner}>
                      <td><strong>{owner.owner}</strong></td><td>{owner.total}</td><td>{owner.open}</td>
                      <td className={owner.overdue ? 'danger-text' : ''}>{owner.overdue}</td>
                      <td className={owner.dueSoon ? 'amber-text' : ''}>{owner.dueSoon}</td>
                      <td>{owner.completed}</td>
                      <td><div className="progress-cell"><div><span style={{ width: `${owner.completionRate}%` }} /></div><strong>{owner.completionRate}%</strong></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function PageTitle({ title, subtitle, actions }) {
  return <div className="page-title"><div><h1>{title}</h1><p>{subtitle}</p></div>{actions && <div className="page-actions">{actions}</div>}</div>;
}
function Loading() { return <div className="loading"><RefreshCw className="spin" size={24} /><span>Loading tracker data...</span></div>; }
function Empty() { return <div className="empty"><FileSpreadsheet size={30} /><strong>No projects found</strong><span>Change the filters or add a new project.</span></div>; }

export default function ProjectTracker() {
  const { user } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState(null);

  const isAdmin = ['admin', 'super_admin'].includes(String(user?.role || '').toLowerCase().trim());

  const notify = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 4500);
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="project-tracker-shell">
          <div className="tracker-access-denied">
            <ShieldCheck size={34} />
            <h2>Admin access required</h2>
            <p>The Project Tracker is available only to administrators.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="project-tracker-shell">
        <div className="project-tracker-nav">
          <button className={page === 'dashboard' ? 'active' : ''} onClick={() => setPage('dashboard')}>
            <LayoutDashboard size={18} /> Dashboard
          </button>
          <button className={page === 'projects' ? 'active' : ''} onClick={() => setPage('projects')}>
            <FolderKanban size={18} /> Projects
          </button>
          <button className={page === 'owners' ? 'active' : ''} onClick={() => setPage('owners')}>
            <Users size={18} /> Owner Scorecard
          </button>
        </div>

        {page === 'dashboard' && <Dashboard refreshKey={refreshKey} user={{...user, role: String(user?.role || '').toLowerCase()}} notify={notify} />}
        {page === 'projects' && <Projects refreshKey={refreshKey} setRefreshKey={setRefreshKey} notify={notify} user={{...user, role: String(user?.role || '').toLowerCase()}} />}
        {page === 'owners' && <Owners refreshKey={refreshKey} />}
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    </Layout>
  );
}
